import type { Doc } from "../../../shared/types.ts";

// Peças puras da exportação/importação: leitor de zip, Markdown → doc TipTap e CSV.

type Node = { type: string; attrs?: Record<string, unknown>; content?: Node[]; text?: string };

const inline = (s: string): Node[] | undefined => {
  const out: Node[] = [];
  s.split("\n").forEach((line, i) => {
    if (i) out.push({ type: "hardBreak" });
    if (line) out.push({ type: "text", text: line }); // ProseMirror não aceita texto vazio
  });
  return out.length ? out : undefined;
};
const para = (s: string): Node => ({ type: "paragraph", content: inline(s) });

/** Texto puro → doc (um parágrafo por linha), como o servidor faz em POST /v1/cards {note}. */
export const textDoc = (s: string): Doc => ({ type: "doc", content: s.split(/\r?\n/).map(para) });

const LIST = /^\s*(?:([-*+])|\d+[.)])\s+(?:\[([ xX])\]\s+)?(.*)$/;

/**
 * Markdown → doc TipTap (aceite 14): títulos, listas (marcadores, numeradas, tarefas), bloco de código, linha e
 * parágrafos. O resto (negrito, links, tabelas, citações...) fica como texto, igual ao arquivo. [[ ]] é texto: o
 * servidor deriva as ligações do contentText.
 * ponytail: listas aninhadas viram itens da mesma lista; aninhar se a importação do Obsidian pedir.
 */
export function markdownDoc(md: string): Doc {
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  const out: Node[] = [];
  let text: string[] = [];
  let list: Node | null = null;
  const flush = () => {
    if (text.length) out.push(para(text.join("\n")));
    text = [];
    list = null;
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fence = line.match(/^\s*```\s*(\S*)/);
    const head = line.match(/^(#{1,6})\s+(.*)$/);
    const item = line.match(LIST);
    if (fence) {
      flush();
      const code: string[] = [];
      while (++i < lines.length && !/^\s*```/.test(lines[i])) code.push(lines[i]);
      const body = code.join("\n");
      out.push({ type: "codeBlock", attrs: { language: fence[1] || null }, content: body ? [{ type: "text", text: body }] : undefined });
    } else if (head) {
      flush();
      out.push({ type: "heading", attrs: { level: head[1].length }, content: inline(head[2].trim()) });
    } else if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      flush();
      out.push({ type: "horizontalRule" });
    } else if (item) {
      if (text.length) flush();
      const kind = item[2] !== undefined ? "taskList" : item[1] ? "bulletList" : "orderedList";
      const current = list as Node | null;
      if (current?.type !== kind) out.push((list = { type: kind, content: [] }));
      const p = { type: "paragraph", content: inline(item[3]) };
      list!.content!.push(kind === "taskList" ? { type: "taskItem", attrs: { checked: item[2] !== " " }, content: [p] } : { type: "listItem", content: [p] });
    } else if (!line.trim()) flush();
    else {
      if (list) list = null;
      text.push(line);
    }
  }
  flush();
  return { type: "doc", content: out };
}

/** CSV legível (RFC 4180): toda célula entre aspas. */
export const csv = (rows: (string | number | null)[][]) => rows.map((r) => r.map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`).join(",")).join("\r\n");

/**
 * Lê um zip (sem compressão, como o client-zip grava, ou deflate) pelo diretório central. Só os pedaços pedidos são lidos.
 * ponytail: sem Zip64 (arquivo acima de 4 GB); entra se alguém exportar tanto.
 */
export async function unzip(file: Blob): Promise<Map<string, Blob>> {
  const view = async (from: number, to: number) => new DataView(await file.slice(from, to).arrayBuffer());
  const tailStart = Math.max(0, file.size - 65_557); // registro final (22) + comentário máximo
  const tail = await view(tailStart, file.size);
  let end = tail.byteLength - 22;
  while (end >= 0 && tail.getUint32(end, true) !== 0x06054b50) end--;
  if (end < 0) throw new Error("not_zip");
  const count = tail.getUint16(end + 10, true);
  const cdStart = tail.getUint32(end + 16, true);
  const cd = await view(cdStart, cdStart + tail.getUint32(end + 12, true));
  const out = new Map<string, Blob>();
  for (let p = 0, n = 0; n < count; n++) {
    if (cd.getUint32(p, true) !== 0x02014b50) throw new Error("not_zip");
    const method = cd.getUint16(p + 10, true);
    const size = cd.getUint32(p + 20, true);
    const nameLen = cd.getUint16(p + 28, true);
    const at = cd.getUint32(p + 42, true);
    const name = new TextDecoder().decode(new Uint8Array(cd.buffer, cd.byteOffset + p + 46, nameLen));
    p += 46 + nameLen + cd.getUint16(p + 30, true) + cd.getUint16(p + 32, true);
    if (name.endsWith("/")) continue;
    const local = await view(at, at + 30);
    const start = at + 30 + local.getUint16(26, true) + local.getUint16(28, true);
    const raw = file.slice(start, start + size);
    if (method === 0) out.set(name, raw);
    else if (method === 8) out.set(name, await new Response(raw.stream().pipeThrough(new DecompressionStream("deflate-raw"))).blob());
    else throw new Error("unsupported_zip");
  }
  return out;
}
